import { useEffect, useState } from "react";
import { Recycle, RotateCcw, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import CriticalPinDialog from "../CriticalPinDialog";

const TYPE_LABEL = {
    orders: "Sipariş", shipments: "Sevkiyat", invoices: "Fatura", rfqs: "RFQ", quotes: "Teklif",
    questions: "Soru", support: "Destek", complaints: "Şikayet", products: "Ürün", campaigns: "Kampanya",
    conversations: "Sohbet", registrations: "Başvuru", videos: "Video", suppliers: "Tedarikçi", companies: "Mağaza",
    featured: "Vitrin", ads: "Reklam", api_keys: "API Anahtarı", notifications: "Bildirim", categories: "Kategori", pages: "Sayfa",
};

const Chip = ({ active, onClick, children, tid }) => (
    <button onClick={onClick} data-testid={tid} className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${active ? "border-primary bg-primary text-primary-foreground" : "hover:border-terra-500/50"}`}>{children}</button>
);

export default function RecycleBinTab() {
    const [items, setItems] = useState(null);
    const [busy, setBusy] = useState(null);
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");
    const [pinAction, setPinAction] = useState(null);

    const load = () => api.get("/admin/recycle-bin").then((r) => setItems(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const restoreItem = (item, pin) => {
        if (pin === undefined) {
            setPinAction({ label: `"${item.label}" (${TYPE_LABEL[item.collection] || item.collection}) kaydını geri yükleme${item.cascade_count ? ` — ${item.cascade_count} bağlı kayıt da geri yüklenecek` : ""}`, run: (p) => restoreItem(item, p) });
            return;
        }
        setBusy(item.bin_id);
        api.post(`/admin/recycle-bin/${item.bin_id}/restore`, {}, { headers: { "X-Critical-Pin": pin } })
            .then(() => { toast.success(`"${item.label}" geri yüklendi`); setPinAction(null); load(); })
            .catch((e) => toast.error(e.response?.data?.detail || "Geri yükleme başarısız"))
            .finally(() => setBusy(null));
    };

    const purgeItem = (item, pin) => {
        if (pin === undefined) {
            setPinAction({ label: `"${item.label}" (${TYPE_LABEL[item.collection] || item.collection}) kaydını KALICI olarak silme — bu işlem geri alınamaz!`, run: (p) => purgeItem(item, p) });
            return;
        }
        setBusy(item.bin_id);
        api.delete(`/admin/recycle-bin/${item.bin_id}`, { headers: { "X-Critical-Pin": pin } })
            .then(() => { toast.success(`"${item.label}" kalıcı olarak silindi`); setPinAction(null); load(); })
            .catch((e) => toast.error(e.response?.data?.detail || "Silinemedi"))
            .finally(() => setBusy(null));
    };

    const emptyAll = (pin) => {
        if (pin === undefined) {
            setPinAction({ label: `Geri dönüşüm kutusundaki ${(items || []).length} kaydın TAMAMINI kalıcı olarak silme — bu işlem geri alınamaz!`, run: (p) => emptyAll(p) });
            return;
        }
        setBusy("all");
        api.delete("/admin/recycle-bin", { headers: { "X-Critical-Pin": pin } })
            .then((r) => { toast.success(`${r.data.deleted} kayıt kalıcı olarak silindi`); setPinAction(null); load(); })
            .catch((e) => toast.error(e.response?.data?.detail || "İşlem başarısız"))
            .finally(() => setBusy(null));
    };

    const types = [...new Set((items || []).map((i) => i.collection))];
    const filtered = (items || []).filter((i) => {
        const q = search.toLowerCase();
        const matchesQ = !q || (i.label || "").toLowerCase().includes(q) || (i.record_id || "").toLowerCase().includes(q) || (i.deleted_by || "").toLowerCase().includes(q);
        return matchesQ && (typeFilter === "all" || i.collection === typeFilter);
    });

    return (
        <div data-testid="recycle-bin-tab">
            <div className="mb-4 flex flex-wrap items-center gap-2">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Kayıt adı, kimlik veya silen ara..." className="h-9 w-64 pl-8 text-sm" data-testid="recycle-search" />
                </div>
                <Chip active={typeFilter === "all"} onClick={() => setTypeFilter("all")} tid="recycle-filter-all">Tümü</Chip>
                {types.map((t) => <Chip key={t} active={typeFilter === t} onClick={() => setTypeFilter(t)} tid={`recycle-filter-${t}`}>{TYPE_LABEL[t] || t}</Chip>)}
                <span className="ml-auto text-xs text-muted-foreground">{filtered.length} kayıt • süresiz saklanır</span>
                {(items || []).length > 0 && (
                    <Button size="sm" variant="outline" className="text-destructive" disabled={!!busy} onClick={() => emptyAll()} data-testid="recycle-empty-all">
                        <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Tümünü Boşalt
                    </Button>
                )}
            </div>
            {!items ? (
                <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded-2xl bg-muted" />)}</div>
            ) : !items.length ? (
                <div className="rounded-2xl border bg-card p-16 text-center shadow-sm" data-testid="recycle-empty">
                    <Recycle className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                    <p className="text-sm font-semibold">Geri dönüşüm kutusu boş</p>
                    <p className="mt-1 text-xs text-muted-foreground">Admin panelinden silinen kayıtlar burada süresiz saklanır; dilediğinizde geri yükleyebilir veya kalıcı silebilirsiniz.</p>
                </div>
            ) : (
                <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
                    <div className="max-h-[520px] overflow-x-auto overflow-y-auto">
                        <table className="w-full text-sm" data-testid="recycle-table">
                            <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                                <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                                    <th className="p-4">Tür</th><th className="p-4">Kayıt</th><th className="p-4">Silinme</th><th className="p-4">Silen</th><th className="p-4">Bağlı</th><th className="p-4 text-right">İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((i) => (
                                    <tr key={i.bin_id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`recycle-row-${i.bin_id}`}>
                                        <td className="p-4"><Badge variant="outline" className="border-amber-200 bg-amber-100 text-[10px] text-amber-800" data-testid={`recycle-type-${i.bin_id}`}>{TYPE_LABEL[i.collection] || i.collection}</Badge></td>
                                        <td className="p-4">
                                            <p className="font-semibold">{i.label}</p>
                                            <p className="text-xs text-muted-foreground">{i.record_id}</p>
                                        </td>
                                        <td className="p-4 text-muted-foreground">{fmtDate(i.deleted_at)}</td>
                                        <td className="p-4 text-xs text-muted-foreground">{i.deleted_by}</td>
                                        <td className="p-4">{i.cascade_count > 0 ? <Badge variant="outline" className="border-blue-200 bg-blue-100 text-[10px] text-blue-800">+{i.cascade_count} kayıt</Badge> : <span className="text-xs text-muted-foreground">—</span>}</td>
                                        <td className="p-4 text-right">
                                            <div className="flex justify-end gap-1.5">
                                                <Button size="sm" variant="outline" className="text-emerald-700" disabled={busy === i.bin_id} onClick={() => restoreItem(i)} data-testid={`recycle-restore-${i.bin_id}`}>
                                                    <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Geri Yükle
                                                </Button>
                                                <Button size="sm" variant="outline" className="text-destructive" disabled={busy === i.bin_id} onClick={() => purgeItem(i)} data-testid={`recycle-purge-${i.bin_id}`}>
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {!filtered.length && <tr><td colSpan={6} className="p-16 text-center text-sm text-muted-foreground">Filtreye uyan kayıt yok.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            <CriticalPinDialog open={!!pinAction} onClose={() => setPinAction(null)} onSubmit={(pin) => pinAction?.run(pin)} actionLabel={pinAction?.label || ""} busy={!!busy} />
        </div>
    );
}
